import { SubmissionError, getFormValues } from 'redux-form';
import { replace } from 'react-router-redux';

import forEach from 'lodash/forEach';
import pick from 'lodash/pick';
import get from 'lodash/get';
import last from 'lodash/last';

import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';

import {
	createSelectOptionsFromSysEnums,
	createSelectValuesUseIntersection,
	createSelectOptionsFromSysEnumsForCountries
} from '../../../../shared/form-elements/select';
import { actions as parentActions } from '../../../editor';

const defaultInitialState = {
	currentLanguage: 'en',
	fileToUpload: null,
	disabledFields: [],
	fields: {
		title: '',
		unique_title: '',
		country_id: null,
		offer_id: null,
		language: null,
		landing_id: null,
		transit_id: null,
		traffic_type: null,
		mark: null,
		affiliate_statuses: null,
		status: null,
		description: ''
	}
};

const createGeneralForm = ({
	namespace,
	stateNodeName
}) => {
	// action types
	const MAIN_FORM_CHAIN_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const MAIN_FORM_CHAIN_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;
	const MAIN_FORM_CHANGE_LANGUAGE = `${namespace}/${stateNodeName}-change-language`;
	const MAIN_FORM_TRANSLATION_REQUEST_SUCCESS = `${namespace}/${stateNodeName}-translation-request-success`;
	const MAIN_FORM_TRANSLATION_REQUEST_ERROR = `${namespace}/${stateNodeName}-translation-request-error`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		mainFormSubmit: (formData) => (dispatch, getState) => {
			const { data } = getState().chainEdit;
			const formData = getFormValues('chainMainForm')(getState());
			const language = get(formData, ['language', '0', 'value']);
			const country_id = get(formData, ['country_id', '0', 'value']);
			const offer_id = get(formData, ['offer_id', '0', 'value']);
			const offer_label = get(formData, ['offer_id', '0', 'label']);
			const landing_id = get(formData, ['landing_id', '0', 'value']);
			const transit_id = get(formData, ['transit_id', '0', 'value']);
			const title = get(formData, 'unique_title');
			const name = offer_label ? `${offer_label}_${transit_id}_${landing_id}_${language}_${title}` : '';
			let affiliate_ids = get(formData, ['affiliate_ids'], []);
			let affiliate_statuses = get(formData, ['affiliate_statuses'], []);

			if (formData && formData.affiliate_ids && formData.affiliate_ids.length !== 0) {
				affiliate_ids = affiliate_ids.map(item => item.value);
			}

			if (formData && formData.affiliate_statuses && formData.affiliate_statuses.length !== 0) {
				affiliate_statuses = affiliate_statuses.map(item => item.value);
			}

			const mark = get(formData, ['mark', '0', 'value']);
			const traffic_type = get(formData, ['traffic_type', '0', 'value']);
			const status = get(formData, ['status', '0', 'value']);
			const rootLang = get(getState(), ['root', 'lists', 'languages'], []);
			let fullComment = rootLang.length != 0 ? rootLang.map(elem => {
				return { [`${elem.id}`]: get(formData, [`${elem.id}`], null) };
			}).reduce((carr, acc) => ({ ...carr, ...acc })) : null;

			let ajaxData = {
				name,
				language,
				country_id,
				offer_id,
				status,
				traffic_type,
				landing_id,
				transit_id,
				affiliate_ids,
				affiliate_statuses,
				mark,
				comment: fullComment
			};

			const url = data.id ? `${endpoints.CHAINS.BASE}/${data.id}` : endpoints.CHAINS.BASE;
			const method = data.id ? 'put' : 'post';

			if (data.id) {
				confirm('Do you want to update the "Updated_at" chain date?\nNote: All other changes will be saved anyway!')
					? ajaxData = { ...ajaxData, shouldUpdate: true }
					: ajaxData = { ...ajaxData, shouldUpdate: false };
			}

			const request = http(
				{
					url,
					method,
					actions: [
						MAIN_FORM_CHAIN_SUBMIT_SUCCESS,
						MAIN_FORM_CHAIN_SUBMIT_ERROR
					],
					data: ajaxData
				}
			);

			return request(dispatch).then(action => {
				if (action.type === MAIN_FORM_CHAIN_SUBMIT_SUCCESS) {
					// @todo Adhock for redux-form submit functionality.
					setTimeout(dispatch(replace(`/chaining/editor/${action.payload.data.id}`)), 100);
					return action;
				}
				if (action.type === MAIN_FORM_CHAIN_SUBMIT_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					throw new SubmissionError(prepareFormErrors(responseErrors));
				}
			})
				.then(action => {
					const { pathname } = window.location;
					const lastParams = parseInt(last(pathname.split('/')));
					dispatch(parentActions.toggleProgressBar());
					dispatch(parentActions.fetchData(lastParams));
				});
		},

		mainFormChangeLanguage: lang => (dispatch, getState) => {
			const actionChangeLang = { type: MAIN_FORM_CHANGE_LANGUAGE, payload: lang };
			return dispatch(actionChangeLang);
		},

		mainFormSubmitTranslation: formData => (dispatch, getState) => {
			const { mainForm: { mustTranslateFields, currentLanguage }, offer } = getState().chainEdit;

			const fields = pick({
				...mustTranslateFields,
				...formData
			}, Object.keys(mustTranslateFields));

			const request = actions.translationRequest(offer.id, currentLanguage, 'put', fields);

			return dispatch(request).then(action => {
				if (action.type === MAIN_FORM_TRANSLATION_REQUEST_SUCCESS) {
					dispatch(actions.mainFormChangeLanguage(currentLanguage));
				}

				if (action.type === MAIN_FORM_TRANSLATION_REQUEST_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					throw new SubmissionError(prepareFormErrors(responseErrors));
				}
			});
		}
	};

	// reducers
	const reducers = {
		[MAIN_FORM_CHANGE_LANGUAGE]: (state, action) => {
			const { payload, meta } = action;
			return {
				...state,
				showProgressBar: false,
				mainForm: {
					...state.mainForm,
					currentLanguage: payload,
					mustTranslateFields: Object.assign(
						{},
						state.mainForm.mustTranslateFields,
						meta ? meta : initialState.mustTranslateFields
					)
				}
			};
		},
		[MAIN_FORM_CHAIN_SUBMIT_ERROR]: errorReducerFn,
		[MAIN_FORM_TRANSLATION_REQUEST_ERROR]: errorReducerFn
	};

	// selectors
	const selectors = {
		getMainFormData: state => {
			const formData = getFormValues('chainMainForm')(state);
			const statusesSelectOptions = createSelectOptionsFromSysEnums(state.root.lists['chain-statuses']);
			const markSelectOptions = createSelectOptionsFromSysEnums(state.root.lists['chain-marks']);
			const language = get(state, ['chainEdit', 'chain', 'language']);
			const traffic_type = get(state, ['chainEdit', 'chain', 'traffic_type']);
			const tempTitle = get(state, ['chainEdit', 'chain', 'name'], '').split('_');
			const unique_title = tempTitle[tempTitle.length - 1];
			const affiliate_statuses = get(state, ['chainEdit', 'chain', 'affiliate_statuses'], [])
				.map(item => ({ value: item, label: item }));
			const status = get(state, ['chainEdit', 'chain', 'status']);
			const mark = get(state, ['chainEdit', 'chain', 'mark']);
			const offer_id = createSelectOptionsFromSysEnums([get(state, ['chainEdit', 'chain', 'offer'], {})]);
			const landing = createSelectOptionsFromSysEnums([get(state, ['chainEdit', 'chain', 'landing'], {})]);
			const transit = createSelectOptionsFromSysEnums([get(state, ['chainEdit', 'chain', 'transit']) || {}]);
			const country = createSelectOptionsFromSysEnumsForCountries([get(state, ['chainEdit', 'chain', 'country'], {})]);
			const comment = get(state, ['chainEdit', 'chain', 'comment'], []);
			const { pathname } = window.location;
			const id = last(pathname.split('/')) ? last(pathname.split('/')) : '';

			let disabledFields = getDisabledFields(formData);

			if (id) {
				disabledFields = ['country_id', 'landing_id', 'transit_id', 'offer_id', 'language'];
			}
			return {
				disabledFields,
				initialValues: {
					// ...state.chainEdit.data,
					language: createSelectOptionsFromSysEnums(state.root.lists['landing-languages'])
						.filter(item => item.value == language),
					status: createSelectValuesUseIntersection(statusesSelectOptions, [status]),
					mark: createSelectValuesUseIntersection(markSelectOptions, [mark]),
					unique_title,
					offer_id,
					country_id: country,
					landing_id: landing,
					transit_id: transit,
					affiliate_statuses,
					...comment,
					affiliate_ids: createSelectOptionsFromSysEnums(get(state, ['chainEdit', 'chain', 'affiliates'])),
					traffic_type: createSelectOptionsFromSysEnums(state.root.lists['traffic-types'])
						.filter(item => item.value == traffic_type)
				}
			};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const prepareFormErrors = errors => {
	const validationsErrors = {};
	forEach(errors, value => {
		if (value.dataPath.length == 0) {
			let key = value.message.substring(value.message.lastIndexOf(' ') + 1);
			validationsErrors[key] = value.message;
		}
		else {
			let key = value.dataPath.slice(1);
			validationsErrors[key] = value.message;
		}

	});
	return validationsErrors;
};

const getDisabledFields = (formData) => {
	let disabledFields = [];
	if (formData && formData.offer_id && formData.offer_id[0] && formData.offer_id[0].value == undefined) {
		disabledFields = ['landing_id', 'transit_id'];
	}
	if (formData && formData.country_id && formData.country_id[0] && formData.country_id[0].value == undefined) {
		disabledFields = ['landing_id', 'transit_id', 'offer_id'];
	}
	return disabledFields;
};

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return ({ ...state, error: true, showProgressBar: false });
	}
	return state;
};

export default createGeneralForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/chaining/components/forms/general/index.js